import type { FailCodeEntity } from '../entities';

import { StsBackendOperation } from '../../sts-backend-operation';

/**
 * Parameters for looking up specific fail codes within a given hierarchy path or by part number.
 */
export type LookupFailCodesParams = {
    /** The figure code to filter fail codes by. */
    figureCode?: string;

    /** The vehicle model code (e.g. "GUE"). */
    modelCode?: string;

    /** The vehicle model year (e.g. "2024"). */
    modelYear?: string;

    /** The vehicle option/trim code (e.g. "01"). */
    optionCode?: string;

    /** The part number to filter fail codes by. */
    partNumber?: string;

    /** The numeric identifier of the section to search within. */
    sectionNumber?: number;

    /** Optional abort signal for cancellation. */
    signal?: AbortSignal;

    /** The numeric identifier of the subsection to search within. */
    subsectionNumber?: number;
};

/**
 * Look up fail codes for a vehicle by section, subsection, and figure (or by part number).
 * Used as the final step in the lookup-by-section and search-by-part flows to retrieve
 * the matching fail codes.
 */
export class LookupFailCodes extends StsBackendOperation {
    /**
     * Look up fail codes from the API and return them as domain entities.
     *
     * @param params - the vehicle identifiers and hierarchy path or part number
     * @returns the matching fail codes as domain entities
     */
    async exec(params: LookupFailCodesParams): Promise<FailCodeEntity[]> {
        const { signal, ...request } = params;
        const response = await this.client.lookupFailCodes(request, signal);

        if (!response.success) {
            return [];
        }

        return response.data.failCodes ?? [];
    }
}
